import { ReactElement, ReactNode, useEffect, useRef, useState } from 'react'

import { Tooltip } from '~/components/component-library'
import { cn } from '~/utils/tailwind-utils'

export const TruncateWithTooltip = ({
  children,
  content,
  className,
  innerClassName,
}: {
  children: ReactElement
  content?: ReactNode
  className?: string
  innerClassName?: string
}) => {
  const { ref, truncated } = useTruncationCheck(children)

  // Toggle isDisabled rather than wrapping conditionally — Radix loses its
  // trigger when the child remounts, which made the tooltip flash and vanish.
  return (
    <Tooltip
      isDisabled={!truncated}
      content={
        <div
          className="max-w-[248px] break-words"
          onClick={(event) => {
            event.stopPropagation()
          }}
        >
          {content}
        </div>
      }
      triggerClassName={cn('flex w-full items-center', className)}
    >
      <div className={cn('w-full truncate', innerClassName)} ref={ref}>
        {children}
      </div>
    </Tooltip>
  )
}

function useTruncationCheck(children?: ReactNode) {
  const ref = useRef<HTMLDivElement>(null)
  const [truncated, setTruncated] = useState(false)

  // eslint-disable-next-line local/noUseEffect -- ResizeObserver lifecycle
  useEffect(() => {
    const el = ref.current
    if (!el) return

    const update = () => setTruncated(el.scrollWidth > el.clientWidth)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [children])

  return { ref, truncated }
}
